import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { DetailsDashboard } from '../../base-details-panel/details.dashboard';
import { DashboardLeerling, LeerlingDetail } from '../../Details';
import { BasicFilterExpression, CompoundFilterExpression, FilterExpression, InFilterExpression } from '../../../services/data.service';
import { intersection, last, memoize, sortBy, union } from 'lodash-es';
import { ToetsA, ToetsComponent, ToetsI } from '../../../dashboards/toets/toets.component';
import { map, take } from 'rxjs/operators';
import { DataRow, getInitialAttributes } from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { ColumnDef, createColumnDef, createDefaultFooterCellDef, TableModel } from '../../../shared/components/table/table/table.model';
import { isFirstAtLevelTabulated, Level, Path } from '../../../services/data-tree';
import { MultilineCellComponent } from '../../../shared/components/table/cells/multiline-cell/multiline-cell.component';
import { MultiAggregator, SingleAggregator, sumOver, weightedAverage } from '../../../services/aggregation';
import { lastValueFrom, ReplaySubject } from 'rxjs';
import { DashboardContext } from '../../../shared/dashboard/base-dashboard/dashboard-context';
import { ExportOptions } from '../../../services/export.service';
import { CijferkolomKeuze, DashboardVariant, Dossier } from '../../../services/weergave-opties';
import { AttrPath, Cijferkolomtype } from '@cumlaude/metadata';
import { CijferlijstCellComponent } from '../../../shared/components/table/cells/cijferlijst-cell/cijferlijst-cell.component';
import { getSchooljaarKort, Option } from '@cumlaude/shared-utils';
import { getCellPath, PivotTableComponent } from '../../../shared/dashboard/pivot-table/pivot-table.component';
import { PivotSeriesFooterCellComponent } from '../../../shared/components/table/cells/pivot-series-footer-cell/pivot-series-footer-cell.component';
import { LabelCellComponent } from '../../../shared/components/table/cells/label-cell/label-cell.component';
import { DataCellComponent } from '../../../shared/components/table/cells/data-cell/data-cell.component';
import { att } from '../../../services/measures';
import { ToetsData } from '../../../shared/components/table/cells/toets-periode-cell/toets-periode-cell.component';
import { AsyncPipe } from '@angular/common';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { DashboardHeaderComponent } from '../../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../../filter-panel/filter-panel.component';
import { CijferlijstData } from '../../../services/cijfers';
import { FilterBarComponent } from '../../../filter-bar/filter-bar.component';
import { FilterName } from '../../../services/filter-name';
import { LegendaComponent } from '../../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

@Component({
	selector: 'app-leerling-cijferlijst-details-panel',
	templateUrl: './leerling-cijferlijst-details-panel.component.html',
	styleUrls: ['./leerling-cijferlijst-details-panel.component.scss'],
	imports: [
		FilterPanelComponent,
		DashboardHeaderComponent,
		PivotTableComponent,
		AsyncPipe,
		FilterBarComponent,
		SingleSelectDropdownComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class LeerlingCijferlijstDetailsPanelComponent extends ToetsComponent implements OnInit, DetailsDashboard<LeerlingDetail> {
	leerling$ = new ReplaySubject<DashboardLeerling>(1);

	override fixedBeforeGroups = 1;

	override flexibleMaxGroups = 0;

	override groupsAsTitles = [];

	actueelFilters: FilterName[] = [
		'cf_fun_nm_schooljaar_leerfase', //
		'cf_is_alternatievenormering',
		'x_details_periode',
		'cf_co_kolom',
		'cf_nm_vak',
	];

	historieFilters: FilterName[] = [
		'cf_fun_nm_schooljaar_leerfase', // is onzichtbaar en filtert niet, maar is alleen om het schooljaar van de actueel-variant vast te houden
		'cf_is_alternatievenormering',
	];

	variant = this.qp.signal('variant');

	historieSubgroups: AttrPath[] = [['cf_nm_schooljaar'], ['cf_nm_klas'], ['cf_nm_kolomtype']];

	override readonly cijferkolomKeuzeOpties = signal<Option<CijferkolomKeuze>[]>(
		[CijferkolomKeuze.ALLE, CijferkolomKeuze.TOETS, CijferkolomKeuze.ADVIES, CijferkolomKeuze.GEMIDDELDE].map((val) => new Option(val))
	);

	@ViewChild(LegendaComponent)
	legendaComponent?: LegendaComponent;

	ngOnInit() {
		this.subscriptions.push(
			this.filterService.observe('cf_fun_nm_schooljaar_leerfase').subscribe(([val]) => this.qp.dispatch('schooljaar', val.split(' ')[0]))
		);
	}

	override createMeasureColumns(
		_context: DashboardContext<ToetsI, ToetsA, LeerlingCijferlijstDetailsPanelComponent>
	): ColumnDef<DataRow<ToetsA>>[] {
		return [];
	}

	override createPivotColumns(
		columnRoot: Level<ToetsA, number[]>,
		context: DashboardContext<ToetsI, ToetsA, LeerlingCijferlijstDetailsPanelComponent>
	): ColumnDef<DataRow<ToetsA>>[] {
		if (this.variant() === DashboardVariant.ACTUEEL) {
			return super.createPivotColumns(columnRoot, context);
		} else {
			return columnRoot.r.map((path) => this.createHistorieColumn(path, context));
		}
	}

	override getRowClickUrl(_context: DashboardContext<ToetsI, ToetsA, ToetsComponent>, _path: Path<ToetsA, number[]>) {
		// Geen zoom-klik voor dit dashboard
		return undefined;
	}

	protected override createPeriodeColumn(
		lvl: Level<ToetsA, number[]>,
		context: DashboardContext<ToetsI, ToetsA, LeerlingCijferlijstDetailsPanelComponent>
	): ColumnDef<DataRow<ToetsA>> {
		const columnDef = super.createPeriodeColumn(lvl, context);

		columnDef.extraFooter = createDefaultFooterCellDef();
		columnDef.extraFooter.component = PivotSeriesFooterCellComponent;
		columnDef.extraFooter.class = 'toets-cell';
		columnDef.extraFooter.format = '1.0-0';
		columnDef.extraFooter.getValue = () => mapAttForRapportcijferEnGemiddelde(lvl, 'sum_tekortpunten');

		columnDef.footer.component = PivotSeriesFooterCellComponent;
		columnDef.footer.class = 'toets-cell';
		columnDef.footer.format = '1.1-1';
		columnDef.footer.getValue = () => mapAttForRapportcijferEnGemiddelde(lvl, 'gem_cijfer');

		return columnDef;
	}

	/**
	 * Voor de historie-variant hebben we wel kolommen met 1 cijfer erin.
	 * De kop is het schooljaar (wat niet herhaald wordt als er meerdere kolommen voor zijn). De subkop is de klas (bij laatste gemiddelde) of
	 * SE/CE/Eind.
	 */
	private createHistorieColumn(
		path: Path<ToetsA, number[]>,
		context: DashboardContext<ToetsI, ToetsA, LeerlingCijferlijstDetailsPanelComponent>
	): ColumnDef<DataRow<ToetsA>> {
		const keys = path.map((lvl) => lvl.k);
		const [_root, cf_nm_schooljaar, cf_nm_klas, cf_nm_kolomtype] = keys;

		const colIndex = last(path)!.i;
		const colKey = `kolom-${colIndex}`;
		const coldef = createColumnDef<DataRow<ToetsA>>(colKey);

		const eersteKolomVanSchooljaar = isFirstAtLevelTabulated(path, 1);
		const schooljaar = eersteKolomVanSchooljaar ? getSchooljaarKort(cf_nm_schooljaar!) : '';
		const klasofKolomtype = this.getKlasofKolomtype(<Cijferkolomtype>cf_nm_kolomtype, cf_nm_klas);

		coldef.header.component = MultilineCellComponent;
		coldef.header.getValue = () => [schooljaar, klasofKolomtype];
		coldef.header.class = 'toets-cell';

		coldef.body.component = CijferlijstCellComponent;
		coldef.body.getValue = this.makeCijferlijstData(colIndex, context);
		coldef.body.class = `toets-cell ${eersteKolomVanSchooljaar ? '' : ' opvolger'}`;

		const { sum_tekortpunten, gem_cijfer } = last(path)!.a;

		coldef.extraFooter = createDefaultFooterCellDef();
		coldef.extraFooter.component = DataCellComponent;
		coldef.extraFooter.format = '1.0-0';
		coldef.extraFooter.getValue = () => sum_tekortpunten;
		coldef.extraFooter.class = `value toets-cell ${eersteKolomVanSchooljaar ? '' : ' opvolger'}`;

		coldef.footer.component = DataCellComponent;
		coldef.footer.format = '1.1-1';
		coldef.footer.getValue = () => gem_cijfer;
		coldef.footer.class = `value toets-cell ${eersteKolomVanSchooljaar ? '' : ' opvolger'}`;

		return coldef;
	}

	private getKlasofKolomtype(cf_nm_kolomtype: Cijferkolomtype, cf_nm_klas: string | null): string {
		switch (cf_nm_kolomtype) {
			case Cijferkolomtype.GEMIDDELDE:
				return this.displayService.display(cf_nm_klas);
			case Cijferkolomtype.SE_CIJFER:
				return 'SE';
			case Cijferkolomtype.CE_CIJFER:
				return 'CE';
			case Cijferkolomtype.EINDCIJFER:
				return 'Eind';
			default:
				return cf_nm_kolomtype;
		}
	}

	private makeCijferlijstData(
		colIndex: number,
		context: DashboardContext<ToetsI, ToetsA, LeerlingCijferlijstDetailsPanelComponent>
	): (row: DataRow<ToetsA>) => CijferlijstData | null {
		const { subgroupNames, measureNames } = context;
		return (row: DataRow<ToetsA>) => {
			const kolomPath = getCellPath(row._path, colIndex);
			if (!kolomPath) return null;

			return getInitialAttributes<ToetsI>(subgroupNames, measureNames, kolomPath);
		};
	}

	protected override makeToetsData(
		attrs: ToetsI,
		_path: Path<ToetsA, number[]>,
		_context: DashboardContext<ToetsI, ToetsA, ToetsComponent>
	): ToetsData {
		const {
			cf_m_nr_cijfer,
			cf_m_nr_decimalen,
			clc_m_abb_label_avg,
			clc_m_is_voldoende_avg,
			cf_m_abb_kolomkop_agg,
			cf_m_co_kolom_agg,
			cf_m_des_kolom_agg,
			cf_m_nr_weging_agg,
			cf_m_nm_bijzonderheid,
			cf_nm_kolomtype,
			cf_m_is_voldoende,
		} = attrs;
		return {
			cf_m_nr_cijfer,
			cf_m_nr_decimalen,
			clc_m_abb_label_avg,
			clc_m_is_voldoende_avg,
			cf_m_nm_bijzonderheid,
			cf_nm_kolomtype,
			cf_m_is_voldoende,
			abb_kolomkop: cf_m_abb_kolomkop_agg,
			co_kolom: cf_m_co_kolom_agg,
			des_kolom: cf_m_des_kolom_agg,
			nr_leerlingen: undefined,
			nr_weging: cf_m_nr_weging_agg,
			perc_onvoldoende: undefined,
		};
	}

	doDetailsExport(exportOptions: ExportOptions) {
		lastValueFrom(this.leerling$.pipe(take(1))).then(({ leerling }) =>
			this.doExport(
				this.filterExpressions(),
				this.getVariantPermanentFilters(this.cijferkolomKeuze(), this.dossier(), leerling, this.variant()),
				exportOptions
			)
		);
	}

	public sortHistorieKolommen(colPath: (string | null)[], keys0: (string | null)[], keys1: (string | null)[]): (string | null)[] {
		const allKeys = union(keys0, keys1);

		//cf_nm_kolomtype
		if (colPath.length == 3) {
			return intersection(
				[
					Cijferkolomtype.PERIODEGEMIDDELDE,
					Cijferkolomtype.GEMIDDELDE,
					Cijferkolomtype.RAPPORTCIJFER,
					Cijferkolomtype.SE_CIJFER,
					Cijferkolomtype.CE_CIJFER,
					Cijferkolomtype.EINDCIJFER,
				],
				allKeys
			);
		}

		return [...sortBy(allKeys)];
	}

	protected override singleAggregators: Partial<{ [ai in keyof ToetsA]: SingleAggregator<ToetsI, ToetsA[ai]> }> = {
		sum_tekortpunten: sumOver('cf_m_nr_tekortpunten'),
	};

	protected override multiAggregators: MultiAggregator<keyof ToetsA, ToetsI, ToetsA, number | null>[] = [
		weightedAverage('gem_cijfer', 'cf_m_nr_cijfer'),
	];

	getVariantPermanentFilters = memoize(this._getVariantPermanentFilters, (c, d, l, v) => JSON.stringify([c, d, l.lb_nr_leerling, v]));

	private _getVariantPermanentFilters(
		_cijferkolomKeuze: CijferkolomKeuze,
		_dossier: Dossier,
		leerling: LeerlingDetail,
		variant: DashboardVariant
	): FilterExpression[] {
		const huidigeLeerling = new BasicFilterExpression(['cf_nr_leerling'], leerling.lb_nr_leerling);
		const geenToets = new CompoundFilterExpression(
			[
				new InFilterExpression(['cf_nm_kolomtype'], [Cijferkolomtype.SE_CIJFER, Cijferkolomtype.CE_CIJFER, Cijferkolomtype.EINDCIJFER]),
				new CompoundFilterExpression([
					new BasicFilterExpression(['cf_nm_kolomtype'], Cijferkolomtype.GEMIDDELDE),
					new BasicFilterExpression(['cf_is_laatste_gemiddelde'], 1),
				]),
			],
			'or'
		);

		return variant === DashboardVariant.ACTUEEL ? [...this.permanentFilters(), huidigeLeerling] : [huidigeLeerling, geenToets];
	}

	getSchooljaarLeerfaseFilterOverride = memoize(LeerlingCijferlijstDetailsPanelComponent._getSchooljaarLeerfaseFilterOverride);

	private static _getSchooljaarLeerfaseFilterOverride(schooljaarInfo: string) {
		return { cf_fun_nm_schooljaar_leerfase: schooljaarInfo };
	}

	override enrichTableModel(
		_context: DashboardContext<ToetsI, ToetsA, LeerlingCijferlijstDetailsPanelComponent>,
		tableModel: TableModel<DataRow<ToetsA>>
	) {
		if ([CijferkolomKeuze.ALLE, CijferkolomKeuze.GEMIDDELDE].includes(this.cijferkolomKeuze())) {
			tableModel.showExtraFooters = true;
			const colDef = tableModel.columnDefs[0];
			colDef.extraFooter = createDefaultFooterCellDef();
			colDef.extraFooter.getValue = () => 'Tekortpunten';
			colDef.extraFooter.class = 'label';
			colDef.footer.component = LabelCellComponent;
			colDef.footer.getValue = () => 'Gemiddeld cijfer';
			colDef.footer.class = 'label';
		} else {
			tableModel.showFooters = false;
		}
	}

	setSelected(selected: LeerlingDetail, schooljaar?: string) {
		const permanentFilters: FilterExpression[] = [new BasicFilterExpression(['cf_nr_leerling'], selected.lb_nr_leerling)];
		if (schooljaar) permanentFilters.push(new BasicFilterExpression(['cf_nm_schooljaar'], schooljaar));

		this.subscriptions.push(
			this.dataService
				.getAllFilterValues('/cijfers', ['cf_fun_nm_schooljaar_leerfase'], permanentFilters)
				.pipe(map((result) => result.values.at(-1)))
				.subscribe((value: string | undefined) =>
					this.leerling$.next({
						leerling: selected,
						schooljaarInfo: value ?? selected.lb_nm_schooljaar + ' ' + selected.lb_nm_opleiding + ' ' + selected.lb_nr_leerjaar,
					})
				)
		);
	}
}

function mapAttForRapportcijferEnGemiddelde<Ai extends keyof ToetsA>(lvl: Level<ToetsA, number[]>, attr: Ai): (ToetsA[Ai] | null)[] {
	return lvl.r.map((path) => {
		if (![Cijferkolomtype.GEMIDDELDE, Cijferkolomtype.RAPPORTCIJFER, Cijferkolomtype.PERIODEGEMIDDELDE].includes(<Cijferkolomtype>path[2].k))
			return null;
		return att<Ai, ToetsA>(attr)(path);
	});
}
